// SpotTenderCarrierInvitePage (Carrier realm)
// -----------------------------------------------------------------------------
// - Uses standard tabs layout (src/pages/tabsInpage.css)
// - Keeps state per "dynamic tab" (id = selectedTab) via M_State
// - Three sections are ALWAYS available (Invited / In Quotation / Finished)
// - onGetTab is compatible with the Settings Page pattern (obj, obj2)
// - renderTabs={allowedTabs()} as in Settings
// - Each section renders its own MTable instance with independent session state
// -----------------------------------------------------------------------------

import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  LoadingContext,
  M_StateActions,
  MTabsInpage,
  NavigateContext,
  MTable,
} from 'src/M_Components';

import { hasPermission } from 'src/components/utils/permissionsHelper';

// Standard styles for pages that use MTabsInpage
import 'src/pages/tabsInpage.css';

export default function SpotTenderCarrierInvitePage({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const matches = useMediaQuery('(min-width:900px)');
  const M_State = useSelector((state) => state.StateKeyValue);
  const location = useLocation();
  const { renameTab } = useContext(NavigateContext);
  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  // Opens the tab with the last selected menu item
  // If no session data is found, it defaults to the first tab (menu 1)
  const [menu, setMenu] = useState(1);
  const [refresh, setRefresh] = useState(false);

  // Logical id of the dynamic tab (same pattern as other pages)
  const [id, setId] = useState(() =>
    M_State.find((obj) => obj.key === 'selectedTab')?.value
  );

  // ---------------------------------------------------------------------------
  // Carrier and per-section identifiers
  // ---------------------------------------------------------------------------

  // Try to resolve CarrierId from:
  // 1) M_State (if some parent already stored it)
  // 2) LoadingContext (carrier realm context)
  // 3) LocalStorage session (app.auth.carrier.session)
  const carrierIdFromState = M_State.find((o) => o.key === 'CarrierId')?.value;

  const carrierSession = useMemo(() => {
    try {
      const raw = localStorage.getItem('app.auth.carrier.session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const carrierId =
    carrierIdFromState ??
    ctx.carrierId ??
    carrierSession?.CarrierId ??
    null;

  // Per section manual ids so each MTable has isolated session state
  // (they are used only by the MTable children, not by the tab-session logic)
  const invitedId = useMemo(() => (id ? `${id}-invited` : undefined), [id]);
  const inQuotationId = useMemo(() => (id ? `${id}-inQuotation` : undefined), [id]);
  const finishedId = useMemo(() => (id ? `${id}-finished` : undefined), [id]);

  // ---------------------------------------------------------------------------
  // Filters per section
  // ---------------------------------------------------------------------------

  const baseStatusInvited = 'Inactive eq false and IsFinished eq false and HasSubmitted eq false';
  const baseStatusInQuotation = 'Inactive eq false and IsFinished eq false and HasSubmitted eq true';
  const baseStatusFinished = 'Inactive eq false and IsFinished eq true';

  const invitedFilter =
    carrierId != null
      ? `CarrierId eq ${Number(carrierId)} and ${baseStatusInvited}`
      : baseStatusInvited;

  const inQuotationFilter =
    carrierId != null
      ? `CarrierId eq ${Number(carrierId)} and ${baseStatusInQuotation}`
      : baseStatusInQuotation;

  const finishedFilter =
    carrierId != null
      ? `CarrierId eq ${Number(carrierId)} and ${baseStatusFinished}`
      : baseStatusFinished;

  // Debug helper to see what is being sent to the backend
  console.debug('[SpotTenderCarrierInvite] carrierId:', carrierId);
  console.debug('[SpotTenderCarrierInvite] invitedFilter:', invitedFilter);
  console.debug('[SpotTenderCarrierInvite] inQuotationFilter:', inQuotationFilter);
  console.debug('[SpotTenderCarrierInvite] finishedFilter:', finishedFilter);

  // Flattened role permissions as expected by MTable
  const rolePermissions = roles?.map((r) => r.Value) ?? [];

  // ---------------------------------------------------------------------------
  // View-only action: open TenderConfigCrud in View mode
  // ---------------------------------------------------------------------------

  const CRUD_ROUTE = '/CarrierInviteViewBid';

  const openTenderView = useCallback(
    (row) => {
      // Safety guard: a row must be selected, otherwise we cannot open CRUD
      if (!row) {
        // Reuse the existing toast mechanism to inform the user
        toastMessages && toastMessages('error', '', t('SelectedLine'));
        return;
      }

      // ---------------------------------------------------------------------
      // 1) Persist the "operation" for TenderConfigCrud
      // Even in Carrier realm, TenderConfigCrud still expects an "operation"
      // value in M_State. The Carrier realm will force the page to behave
      // as View-only, since this page will only allow to view tenders,
      // not edit or create.
      // ---------------------------------------------------------------------
      dispatch(M_StateActions.addM_State({ key: 'operation', value: 'View' }));

      // ---------------------------------------------------------------------
      // 2) Build a minimal TenderLine object to hand over to TenderConfigCrud
      //  - Try TenderId from the relation row
      //  - Fallback to nested Tender.Id
      //  - Fallback to row.Id as a last resort
      // ---------------------------------------------------------------------
      const tenderLine = {
        Id: row.TenderId ?? row.Tender?.Id ?? row.Id,
        Name: row.Tender?.Name ?? row.Name ?? '',
      };

      // Store the TenderLine in M_State so TenderConfigCrud can pick it up
      dispatch(M_StateActions.addM_State({ key: 'TenderLine', value: tenderLine }));

      // ---------------------------------------------------------------------
      // 3) Derive the Carrier Bid "crud mode" from the current tab (menu)
      //  1 -> Invited      => "create"
      //  2 -> In Quotation => "edit"
      //  3 -> Finished     => "view"
      // This metadata will later be read by TenderConfigCrud / CarrierSubmitPage.
      // In this SpotTenderCarrierInvitePage, the scope is ALWAYS "tender".
      // ---------------------------------------------------------------------
      let bidCrudMode = 'view'; // safe default

      if (menu === 1) {
        bidCrudMode = 'create'; // Invited tab
      } else if (menu === 2) {
        bidCrudMode = 'edit';   // In Quotation tab
      } else if (menu === 3) {
        bidCrudMode = 'view';   // Finished tab
      }

      dispatch(
        M_StateActions.addM_State({
          key: 'CarrierBidOrigin',
          value: {
            scope: 'tender',    // This page is Spot (Tender-level) context
            crudMode: bidCrudMode,  // "create" | "edit" | "view" derived from tab
          },
        })
      );

      // ---------------------------------------------------------------------
      // 4) Build the tab label and open TenderConfigCrud
      // use the tender name when present.
      // ---------------------------------------------------------------------
      const baseLabel = t('LabelViewTender');
      const extra = tenderLine.Name || '';
      const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel;

      onCreateTab && onCreateTab(CRUD_ROUTE, tabLabel);
    },
    // Dependencies:
    [dispatch, onCreateTab, menu]
  );



  // this page will only allow to view tenders, so we remove create/edit/delete options
  const removeContextItems = useMemo(
    () => ['Print', 'Copy', 'Create', 'Edit', 'Delete'],
    []
  );

  // Actions available in the context menu
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openTenderView(row)],
      ]),
    [openTenderView]
  );

  //============================================================================================================
  // Upsert session data for the current tab
  // - Exactly the same pattern as SpotTenderPage
  //============================================================================================================
  const upsertSession = (tabId, kv) => {
    if (!tabId) return;
    const current = M_State.find((o) => o.key === tabId)?.value || []; // Get the current session data
    const filtered = current.filter((e) => e.key !== kv.key); // Remove any existing entry for the key
    dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] })); // Update the session data
  };

  //============================================================================================================
  // Function to retrieve session storage data based on session ID
  // - Rehydrates menu/refresh for the dynamic tab (per-tab session)
  //============================================================================================================
  const getSessionStorageData = (sessionID) => {
    if (!sessionID) return;
    ctx.setIsLoad(true);
    const session = M_State.find((obj) => obj.key === sessionID)?.value; // Retrieve session data from M_State
    if (session) {
      const m = session.find((x) => x.key === 'menu')?.value; // Get the menu value from session data
      const ref = session.find((x) => x.key === 'refresh')?.value; // Get the refresh value from session data
      setMenu(typeof m === 'number' ? m : 1); // Set the menu state to the retrieved value or default to 1
      if (ref !== undefined) setRefresh(ref); // Set the refresh state to the retrieved value if it exists
    } else {
      setMenu(1); // Default to menu 1 if no session data is found
    }
    ctx.setIsLoad(false);
  };

  //============================================================================================================
  // EFFECT
  // Rename tab on language change (same as SpotTenderPage)
  //============================================================================================================
  useEffect(() => {
    if (!selectedTab) return; // If no tab is selected, do nothing
    if (!renameTab) return;
    renameTab(String(selectedTab), t('SpotTenders'));
  }, [t, selectedTab, renameTab]);

  //============================================================================================================
  // EFFECT
  // Retrieve session data when the component mounts or when the pathname changes
  //============================================================================================================
  useEffect(() => {
    if (!id) return;
    getSessionStorageData(id); // Call the function to get session data
    setRefresh((r) => !r); // Toggle refresh state to trigger re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  //============================================================================================================
  // EFFECT
  // useEffect for selectedTab changes (mirror of SpotTenderPage which uses M_State)
  //============================================================================================================
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value; // Get the new selected tab ID
    if (newId && newId !== id) {
      setId(newId); // Update the ID state
      getSessionStorageData(newId); // Retrieve session data for the new tab
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  //============================================================================================================
  // EFFECT
  // Persist menu in session for the current dynamic tab
  //============================================================================================================
  useEffect(() => {
    if (!id) return;
    upsertSession(id, { key: 'menu', value: menu });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, menu]);

  //============================================================================================================
  // EFFECT
  // Persist refresh flag in session (kept for compatibility with other pages)
  //============================================================================================================
  useEffect(() => {
    if (!id) return;
    upsertSession(id, { key: 'refresh', value: refresh });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh]);

  // ---------------------------------------------------------------------------
  // Tabs allowed for the current user
  // ---------------------------------------------------------------------------

  // Builds the tabs list based on permissions.
  // Only users with Tenders List or Admin permission will see these tabs.
  const allowedTabs = () => {
    const tabArray = [];

    if (hasPermission(roles, 'Tenders', 'List') || hasPermission(roles, 'Tenders', 'Admin')) {
      tabArray.push(
        {
          id: 1,
          labelTab: t('Spot Tenders Invited'),
          iconTab: <i className="pi pi-envelope" style={{ fontSize: '1.5rem' }} />,
        },
        {
          id: 2,
          labelTab: t('Spot Tenders In Quotation'),
          iconTab: <i className="pi pi-chart-line" style={{ fontSize: '1.5rem' }} />,
        },
        {
          id: 3,
          labelTab: t('Spot Tenders Finished'),
          iconTab: <i className="pi pi-check-square" style={{ fontSize: '1.5rem' }} />,
        }
      );
    }

    return tabArray;
  };

  // ---------------------------------------------------------------------------
  // Render (standard tabsInpage layout)
  // ---------------------------------------------------------------------------

  return (
    // Backward-compat bridge + unified layout classes (visual only)
    <div className="tabsInpage divGlobalSettings">
      <div
        className="tabsInpage__title titleSettings"
        style={{ color: ctx.secondaryColor }}
      >
        {t('SpotTenders')}
      </div>

      {/* Standard body split: sidebar (tabs) + content (panel) */}
      <div className="tabsInpage__body">
        {/* Sidebar column (controlled by CSS: widths, separator, responsive) */}
        <div className="tabsInpage__sidebar">
          <MTabsInpage
            // Tab component for navigation (same pattern as SpotTenderPage)
            type={matches ? 'column' : 'tabs'}
            renderTabs={allowedTabs()}
            firstTabOpen={menu}
            onGetTab={(obj, obj2) => {
              // Settings pattern: desktop uses obj, mobile (tabs) may use obj2
              if (matches) {
                setMenu(obj.id);
              } else {
                setMenu(obj2?.id ?? obj.id);
              }
            }}
            fixedArea={false}
          />
        </div>

        {/* Right content panel. Legacy class kept for bridge; no behavior changes */}
        <div className="tabsInpage__content settingsPageMainDiv">
          {menu === 1 && (
            <SectionInvitedTable
              manualId={invitedId}
              rolePermissions={rolePermissions}
              filter={invitedFilter}
              removeContextItems={removeContextItems}
              replaceFunctions={replaceFunctions}
            />
          )}
          {menu === 2 && (
            <SectionInQuotationTable
              manualId={inQuotationId}
              rolePermissions={rolePermissions}
              filter={inQuotationFilter}
              removeContextItems={removeContextItems}
              replaceFunctions={replaceFunctions}
            />
          )}
          {menu === 3 && (
            <SectionFinishedTable
              manualId={finishedId}
              rolePermissions={rolePermissions}
              filter={finishedFilter}
              removeContextItems={removeContextItems}
              replaceFunctions={replaceFunctions}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Child components: each one owns its own MTable and refresh state
// This mirrors the pattern used by SpotTenderConfig and TenderInQuotation, etc.
// -----------------------------------------------------------------------------

function SectionInvitedTable({ manualId, rolePermissions, filter, removeContextItems, replaceFunctions }) {
  const [refresh, setRefresh] = useState(false);

  return (
    <MTable
      entityName="TenderCarriers/carrier"
      manualId={manualId}
      roleName="Tenders"
      rolePermissions={rolePermissions}
      useSessionStorage={false}
      filtersOnTable
      FILTER={filter || undefined}
      refresh={refresh}
      setRefresh={setRefresh}
      maxRows={20}
      stopSaveRows={false}
      removeContextItems={removeContextItems}
      replaceFunctions={replaceFunctions}
    />
  );
}

function SectionInQuotationTable({ manualId, rolePermissions, filter, removeContextItems, replaceFunctions }) {
  const [refresh, setRefresh] = useState(false);

  return (
    <MTable
      entityName="TenderCarriers/carrier"
      manualId={manualId}
      roleName="Tenders"
      rolePermissions={rolePermissions}
      useSessionStorage={false}
      filtersOnTable
      FILTER={filter || undefined}
      refresh={refresh}
      setRefresh={setRefresh}
      maxRows={20}
      stopSaveRows={false}
      removeContextItems={removeContextItems}
      replaceFunctions={replaceFunctions}
    />
  );
}

function SectionFinishedTable({ manualId, rolePermissions, filter, removeContextItems, replaceFunctions }) {
  const [refresh, setRefresh] = useState(false);

  return (
    <MTable
      entityName="TenderCarriers/carrier"
      manualId={manualId}
      roleName="Tenders"
      rolePermissions={rolePermissions}
      useSessionStorage={false}
      filtersOnTable
      FILTER={filter || undefined}
      refresh={refresh}
      setRefresh={setRefresh}
      maxRows={20}
      stopSaveRows={false}
      removeContextItems={removeContextItems}
      replaceFunctions={replaceFunctions}
    />
  );
}
